/**
 * Engagement blocks — extracted from ActionBlockRenderer.jsx (v119 refactor).
 *
 * Includes: CountdownBlock, FAQItem, VideoPlaylistBlock, PollBlock,
 * NewsletterBlock. Re-exported from ActionBlockRenderer.jsx.
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Timer, ChevronDown, HelpCircle, Copy, Check, Share2, Gift, LogIn } from "lucide-react";
import { _postPublicLead } from "./_shared";
// v149.19 — The playlist renders through the SAME component as the
// `video` block instead of its own YouTube-only iframe, so it gets the
// cover + tap-to-play gate (no third-party iframe until the viewer asks
// for one), non-YouTube URLs, and the existing video analytics.
import { InlineVideoEmbed } from "../InlineVideoEmbed";
// v145.26 — Read the signed-in user so the newsletter block prefills
// the email input. Falls back to empty for anonymous visitors.
import { useAuth } from "../../context/AuthContext";

export function CountdownBlock({ block }) {
  const c = block.config || {};
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const target = c.deadline ? Date.parse(c.deadline) : null;
  const diff = target ? Math.max(0, target - now) : 0;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor(diff / 3600000) % 24;
  const m = Math.floor(diff / 60000) % 60;
  const s = Math.floor(diff / 1000) % 60;
  return (
    <div className="ah-block text-center" data-testid={`block-countdown-${block.block_id}`}>
      {c.label && <p className="font-bold text-sm mb-1">{c.label}</p>}
      <div className="flex items-center justify-center gap-2">
        <Timer className="w-4 h-4 ah-accent" />
        <span className="font-mono font-black text-lg tracking-tight ah-accent">
          {d}d {String(h).padStart(2, "0")}h {String(m).padStart(2, "0")}m {String(s).padStart(2, "0")}s
        </span>
      </div>
      {!target && <p className="text-xs ah-muted mt-1">Set a deadline in the editor.</p>}
    </div>
  );
}

export function FAQItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ah-block" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left flex items-center justify-between gap-2"
      >
        <span className="font-bold text-sm">{q || "Question?"}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="mt-2 text-sm ah-muted leading-relaxed">{a || ""}</p>}
    </div>
  );
}

export function VideoPlaylistBlock({ block, items }) {
  const [idx, setIdx] = useState(0);
  const current = items[idx] || items[0];
  return (
    <div className="space-y-2" data-testid={`block-video-playlist-${block.block_id}`}>
      {current?.url ? (
        <InlineVideoEmbed
          // Re-mount on track change so the cover re-arms for the new video.
          key={current.url}
          url={current.url}
          testid={`block-video-playlist-player-${block.block_id}`}
          blockId={block.block_id}
          surface="bio_block"
          posterUrl={current.thumbnail_url || null}
          cover
        />
      ) : (
        <p className="text-xs ah-muted text-center">Add video URLs in the editor.</p>
      )}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {items.map((v, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className="border-2 px-2 py-1 text-xs font-bold whitespace-nowrap flex-shrink-0"
            style={{ borderColor: "var(--ah-fg)", background: i === idx ? "var(--ah-accent)" : "var(--ah-block-bg)", color: i === idx ? "#fff" : "var(--ah-fg)" }}
            data-testid={`block-video-playlist-item-${block.block_id}-${i}`}
          >
            {v.title || `Video ${i + 1}`}
          </button>
        ))}
      </div>
    </div>
  );
}

export function PollBlock({ block, editable }) {
  const c = block.config || {};
  const [voted, setVoted] = useState(null);
  const [results, setResults] = useState(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const vote = async (optionId) => {
    if (editable) return;
    setVoted(optionId);
    try {
      const parts = window.location.pathname.split("/").filter(Boolean);
      if (parts[0] !== "u" || parts.length < 2) return;
      // v143.13 (Feb 2026) — Support both surfaces the same PollBlock is
      // dropped into:
      //   * Bio hub  → URL is /u/{slug}         (parts.length === 2)
      //   * Action page → URL is /u/{slug}/{page_slug}(/...)
      // The two write to different collections on the backend
      // (`creator_hub_blocks` vs `action_page_blocks`), so we route to
      // the matching endpoint. Before this fix the block early-returned
      // on the bio hub and no vote was ever recorded.
      const [, creator_slug, page_slug] = parts;
      const url = page_slug
        ? `${backend}/api/public/pages/${creator_slug}/${page_slug}/poll/${block.block_id}/vote`
        : `${backend}/api/public/creators/${creator_slug}/poll/${block.block_id}/vote`;
      const res = await fetch(
        url,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ option_id: optionId }),
        },
      );
      if (res.ok) {
        const json = await res.json();
        setResults(json);
      }
    } catch { /* offline */ }
  };

  const options = c.options || [];
  const total = results?.total || 0;
  return (
    <div className="ah-block space-y-2" data-testid={`block-poll-${block.block_id}`}>
      <p className="font-bold text-sm text-center">{c.question || "Poll"}</p>
      {options.map((o) => {
        const count = results?.counts?.[o.id] || 0;
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        const active = voted === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => vote(o.id)}
            disabled={!!voted}
            className="w-full text-left border-2 px-3 py-1.5 relative overflow-hidden"
            style={{ borderColor: "var(--ah-fg)", background: "var(--ah-block-bg)" }}
            data-testid={`block-poll-option-${block.block_id}-${o.id}`}
          >
            {voted && (
              <span
                className="absolute inset-y-0 left-0 opacity-20"
                style={{ width: `${pct}%`, background: "var(--ah-accent)" }}
              />
            )}
            <span className="relative flex items-center justify-between text-sm">
              <span className={active ? "font-bold" : ""}>{o.label}</span>
              {voted && <span className="text-xs font-bold ah-muted">{pct}% ({count})</span>}
            </span>
          </button>
        );
      })}
      {voted && <p className="text-xs ah-muted text-center">Total votes: {total}</p>}
    </div>
  );
}

export function NewsletterBlock({ block, editable }) {
  const c = block.config || {};
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [status, setStatus] = useState("idle");
  // v145.26 — Backfill when auth resolves after mount.
  useEffect(() => {
    if (user?.email) setEmail((v) => v || user.email);
  }, [user?.user_id]);
  const submit = async (e) => {
    e.preventDefault();
    if (editable) return;
    setStatus("submitting");
    try {
      await _postPublicLead(block, { email, intent: "newsletter" });
      setStatus("ok");
      setEmail("");
    } catch { setStatus("err"); }
  };
  return (
    <form onSubmit={submit} className="ah-block" data-testid={`block-newsletter-${block.block_id}`}>
      <div className="flex items-center gap-2 mb-2 justify-center">
        <HelpCircle className="w-4 h-4 ah-accent" />
        <p className="font-bold text-sm">{c.headline || "Join my newsletter"}</p>
      </div>
      {c.subtext && <p className="text-xs ah-muted text-center mb-2">{c.subtext}</p>}
      <div className="flex gap-2">
        <input
          type="email" required placeholder="you@example.com"
          value={email} onChange={(e) => setEmail(e.target.value)}
          className="flex-1 border-2 px-2 py-1 text-sm bg-transparent" style={{ borderColor: "var(--ah-fg)" }}
          data-testid={`newsletter-input-${block.block_id}`}
        />
        <button
          type="submit"
          disabled={status === "submitting" || editable}
          className="border-2 px-3 py-1 font-bold text-xs disabled:opacity-50"
          style={{ borderColor: "var(--ah-fg)", background: "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
          data-testid={`newsletter-submit-${block.block_id}`}
        >
          Join
        </button>
      </div>
      {status === "ok" && <p className="text-xs ah-accent font-bold text-center mt-1">You&apos;re in!</p>}
    </form>
  );

}

// ---------------------------------------------------------------------------
// v143.2 (Feb 2026) — Referral block. Signed-in Linkola users see their
// personal share URL with `?ref=<their_loyalty_account_id>` baked in.
// A successful follow/signup from that link credits the sharer with
// coins via the existing `stamp_referral` → `_fulfil_referral_if_any`
// path already implemented in loyalty_service.py. Anonymous visitors
// see a soft CTA to sign in — no aggressive gate, since the block
// still works organically once they eventually authenticate.
// ---------------------------------------------------------------------------
export function ReferralBlock({ block, editable, hubContext }) {
  const c = block.config || {};
  const backend = process.env.REACT_APP_BACKEND_URL;
  const slug = hubContext?.creator?.slug || hubContext?.slug || null;
  const [state, setState] = useState({ loading: true });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (editable) { setState({ loading: false, editor: true }); return; }
    if (!slug) { setState({ loading: false }); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${backend}/api/public/creators/${encodeURIComponent(slug)}/my-referral-account`,
          { credentials: "include" },
        );
        if (r.status === 401) { if (!cancelled) setState({ loading: false, loggedOut: true }); return; }
        if (!r.ok) { if (!cancelled) setState({ loading: false, error: true }); return; }
        const d = await r.json();
        if (cancelled) return;
        if (!d.eligible) {
          // v143.11 (Feb 2026) — Distinguish the three "not eligible" cases
          // from the backend so the CREATOR viewing their own hub sees a
          // helpful config CTA instead of the same "not running referrals"
          // message a visitor would see. Backend returns:
          //   * `reason: "self"`             — owner viewing own hub
          //   * `reason: "disabled_or_zero"` — loyalty off OR earn=0
          //   * (anything else / missing)    — generic disabled
          if (d.reason === "self") {
            setState({ loading: false, self: true });
          } else if (d.reason === "disabled_or_zero") {
            setState({ loading: false, disabledZero: true });
          } else {
            setState({ loading: false, disabled: true });
          }
          return;
        }
        setState({ loading: false, share_url: d.share_url, coin_name: d.coin_name,
                   earn_amount: d.earn_amount, balance: d.balance });
      } catch { if (!cancelled) setState({ loading: false, error: true }); }
    })();
    return () => { cancelled = true; };
  }, [backend, slug, editable]);

  const doCopy = async () => {
    if (!state.share_url) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Join me on this page",
          text: `Follow ${slug} with me — you'll get updates + I'll get ${state.earn_amount} ${state.coin_name}.`,
          url: state.share_url,
        });
        return;
      }
      await navigator.clipboard.writeText(state.share_url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy — long-press the box below to share manually."); }
  };

  return (
    <div className="ah-block" data-testid={`block-referral-${block.block_id}`}>
      <div className="flex items-center gap-2 mb-2 justify-center">
        <Gift className="w-4 h-4 ah-accent" strokeWidth={2.5} />
        <p className="font-bold text-sm">{c.heading || "Refer a friend, earn coins"}</p>
      </div>
      {c.subtext && (
        <p className="text-xs ah-muted text-center mb-3">{c.subtext}</p>
      )}
      {state.loading && (
        <p className="text-xs ah-muted text-center animate-pulse">Loading your link…</p>
      )}
      {state.editor && (
        <p className="text-xs ah-muted text-center italic">
          Preview only — signed-in visitors will see their personal share link here.
        </p>
      )}
      {state.loggedOut && (
        <div className="text-center space-y-2" data-testid={`block-referral-loggedout-${block.block_id}`}>
          <p className="text-xs ah-muted">
            {c.logged_out_message || "Sign in with your email to get your referral link."}
          </p>
          <a
            href="/auth"
            className="inline-flex items-center gap-1 border-2 px-3 py-1 text-xs font-bold"
            style={{ borderColor: "var(--ah-fg)", background: "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
          >
            <LogIn className="w-3 h-3" /> Sign in
          </a>
        </div>
      )}
      {state.disabled && (
        <p className="text-xs ah-muted text-center italic" data-testid={`block-referral-disabled-${block.block_id}`}>
          This creator isn&apos;t running referrals right now — check back soon!
        </p>
      )}
      {/* v143.11 — Owner viewing own hub. Show a preview + "Set it up" CTA
          so they know why THEIR view of the block looks empty while
          understanding visitors would see their working share link.
          Points to `/app/action-hub/loyalty` where earn_rules.referral
          is configured. */}
      {state.self && (
        <div className="text-center space-y-2" data-testid={`block-referral-self-${block.block_id}`}>
          <p className="text-xs ah-muted italic">
            You&apos;re viewing your own hub — signed-in visitors will see their
            personal share link here.
          </p>
          <a
            href="/app/action-hub/loyalty"
            className="inline-flex items-center gap-1 border-2 px-3 py-1 text-xs font-bold"
            style={{ borderColor: "var(--ah-fg)", background: "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
          >
            Set your referral rewards →
          </a>
        </div>
      )}
      {/* v143.11 — Loyalty off OR referral earn amount = 0. This is what
          the visitor sees when the creator hasn't configured the program
          yet, but for the creator themselves we still route this to the
          `self` state above via the backend, so this branch fires only
          for real visitors. */}
      {state.disabledZero && (
        <p className="text-xs ah-muted text-center italic" data-testid={`block-referral-disabled-zero-${block.block_id}`}>
          This creator hasn&apos;t set up referral rewards yet — check back soon!
        </p>
      )}
      {state.error && (
        <p className="text-xs ah-muted text-center italic" data-testid={`block-referral-error-${block.block_id}`}>
          Couldn&apos;t load your referral link. Try refreshing.
        </p>
      )}
      {state.share_url && (
        <div className="space-y-2" data-testid={`block-referral-ready-${block.block_id}`}>
          <div className="text-center text-[11px] font-bold uppercase tracking-wider ah-muted">
            Earn {state.earn_amount} {state.coin_name} per friend
          </div>
          <button
            type="button"
            onClick={doCopy}
            className="w-full border-2 px-3 py-2 font-bold text-sm inline-flex items-center justify-center gap-2"
            style={{ borderColor: "var(--ah-fg)", background: "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
            data-testid={`block-referral-copy-btn-${block.block_id}`}
          >
            {copied ? <Check className="w-3 h-3" /> : (navigator.share ? <Share2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />)}
            {copied ? "Copied!" : (c.button_label || "Copy my link")}
          </button>
          <input
            type="text"
            value={state.share_url}
            readOnly
            onFocus={(e) => e.target.select()}
            className="w-full border-2 px-2 py-1 text-[10px] font-mono truncate bg-transparent"
            style={{ borderColor: "var(--ah-fg)" }}
            data-testid={`block-referral-share-url-${block.block_id}`}
          />
          {state.balance > 0 && (
            <p className="text-[10px] text-center ah-muted">
              You have <b>{state.balance}</b> {state.coin_name} here
            </p>
          )}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Phase 6: WhatsApp + App-store CTAs
// ---------------------------------------------------------------------------
